import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowLeft, FaArrowRight, FaCheckCircle, FaClock, FaUsers,
  FaIndustry, FaChartLine, FaCode, FaLayerGroup, FaLightbulb,
  FaQuoteLeft,
} from 'react-icons/fa';
import { PROJECTS_DATA } from '../data/projects';
import PageTransition from '../components/PageTransition';
import { fadeInUp, staggerContainer, viewport } from '../utils/animations';

/* ─── helpers ──────────────────────────────────────────────── */
const Section = ({ children, className = '' }) => (
  <motion.div
    variants={fadeInUp}
    initial="hidden"
    whileInView="visible"
    viewport={viewport}
    className={className}
  >
    {children}
  </motion.div>
);

const SectionLabel = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon className="text-gray-500 text-sm" />
    <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</span>
  </div>
);

/* ─── Component ────────────────────────────────────────────── */
const ProjectDetail = () => {
  const { slug } = useParams();
  const project = PROJECTS_DATA.find((p) => p.slug === slug);

  if (!project) return <Navigate to="/projects" replace />;

  const currentIndex = PROJECTS_DATA.findIndex((p) => p.slug === slug);
  const prev = PROJECTS_DATA[currentIndex - 1] || null;
  const next = PROJECTS_DATA[currentIndex + 1] || null;

  return (
    <PageTransition>
      <div className="bg-transparent min-h-screen">

        {/* ── Hero ─────────────────────────────────────── */}
        <div className="relative h-[70vh] min-h-[500px] overflow-hidden">
          <img
            src={project.heroImage}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/60 to-gray-950/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-transparent to-transparent" />

          <div className="relative z-10 h-full flex flex-col justify-end pb-16 container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div variants={staggerContainer} initial="hidden" animate="visible">
              {/* Back link */}
              <motion.div variants={fadeInUp} className="mb-6">
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <FaArrowLeft className="text-xs" /> All Projects
                </Link>
              </motion.div>

              {/* Category + featured */}
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 text-xs font-bold uppercase tracking-widest bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full">
                  {project.category}
                </span>
                {project.featured && (
                  <span className="px-3 py-1 text-xs font-bold uppercase tracking-widest bg-yellow-400/20 border border-yellow-400/30 text-yellow-300 rounded-full">
                    Featured Project
                  </span>
                )}
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 leading-tight max-w-4xl">
                {project.title}
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg text-gray-300 font-medium max-w-2xl">
                {project.tagline}
              </motion.p>
            </motion.div>
          </div>
        </div>

        {/* ── Metrics Bar ──────────────────────────────── */}
        <div className="border-y border-gray-800/60 bg-gray-950/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-800/60">
              {project.metrics.map((m) => (
                <div key={m.label} className="py-7 px-6 text-center">
                  <div className="text-3xl font-black text-white mb-1">{m.value}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────── */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto space-y-20">

            {/* Meta info row */}
            <Section>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: FaClock, label: 'Timeline', value: project.timeline },
                  { icon: FaUsers, label: 'Team Size', value: project.teamSize },
                  { icon: FaIndustry, label: 'Industry', value: project.industry },
                  { icon: FaLayerGroup, label: 'Deliverables', value: `${project.deliverables.length} modules` },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="glass-card rounded-xl p-4 text-center">
                    <Icon className="text-gray-500 text-lg mx-auto mb-2" />
                    <div className="text-white font-bold text-sm">{value}</div>
                    <div className="text-gray-600 text-xs uppercase tracking-widest mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Overview */}
            <Section>
              <SectionLabel icon={FaLightbulb} label="Overview" />
              <h2 className="text-2xl md:text-3xl font-black text-white mb-5">The Opportunity</h2>
              <p className="text-gray-300 text-lg leading-relaxed">{project.overview}</p>
            </Section>

            {/* Challenge */}
            <Section>
              <div className="glass-card rounded-2xl p-8 border border-red-900/20 bg-red-950/5">
                <SectionLabel icon={FaChartLine} label="The Challenge" />
                <h2 className="text-2xl md:text-3xl font-black text-white mb-5">What Was Broken</h2>
                <div className="flex gap-4">
                  <FaQuoteLeft className="text-red-500/40 text-4xl flex-shrink-0 mt-1" />
                  <p className="text-gray-300 text-lg leading-relaxed">{project.problem}</p>
                </div>
              </div>
            </Section>

            {/* Solution */}
            <Section>
              <div className="glass-card rounded-2xl p-8 border border-emerald-900/20 bg-emerald-950/5">
                <SectionLabel icon={FaCode} label="Our Solution" />
                <h2 className="text-2xl md:text-3xl font-black text-white mb-5">How We Solved It</h2>
                <p className="text-gray-300 text-lg leading-relaxed">{project.solution}</p>
              </div>
            </Section>

            {/* Tech Stack */}
            <Section>
              <SectionLabel icon={FaLayerGroup} label="Technology Stack" />
              <h2 className="text-2xl md:text-3xl font-black text-white mb-6">Built With</h2>
              <div className="flex flex-wrap gap-3">
                {project.techStack.map((tech) => (
                  <span
                    key={tech}
                    className="px-4 py-2 text-sm font-semibold bg-gray-800/80 border border-gray-700/60 text-gray-300 rounded-lg hover:border-gray-500 hover:text-white transition-colors duration-200"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </Section>

            {/* Deliverables */}
            <Section>
              <SectionLabel icon={FaCheckCircle} label="Deliverables" />
              <h2 className="text-2xl md:text-3xl font-black text-white mb-6">What We Built</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {project.deliverables.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 glass-card rounded-xl px-5 py-4 border border-gray-800/60"
                  >
                    <FaCheckCircle className="text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-200 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Results */}
            <Section>
              <div className="relative overflow-hidden glass-card rounded-2xl p-8 border border-white/5">
                <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />
                <div className="relative z-10">
                  <SectionLabel icon={FaChartLine} label="Results & Impact" />
                  <h2 className="text-2xl md:text-3xl font-black text-white mb-5">The Outcomes</h2>
                  <p className="text-gray-300 text-lg leading-relaxed mb-8">{project.results}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {project.metrics.map((m) => (
                      <div key={m.label} className="text-center p-4 bg-gray-900/60 rounded-xl border border-gray-800/60">
                        <div className="text-2xl font-black animated-gradient-text">{m.value}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Section>

            {/* Gallery */}
            {project.gallery?.length > 0 && (
              <Section>
                <SectionLabel icon={FaLayerGroup} label="Project Gallery" />
                <h2 className="text-2xl md:text-3xl font-black text-white mb-6">In Action</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.gallery.map((src, i) => (
                    <div key={i} className="rounded-2xl overflow-hidden border border-gray-800/60 h-56">
                      <img
                        src={src}
                        alt={`${project.title} screenshot ${i + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </Section>
            )}

          </div>
        </div>

        {/* ── Prev / Next ───────────────────────────────── */}
        {(prev || next) && (
          <div className="border-t border-gray-800/60">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="flex flex-col sm:flex-row gap-4">
                {prev && (
                  <Link
                    to={`/projects/${prev.slug}`}
                    className="flex-1 group glass-card rounded-2xl p-6 hover:border-gray-600 transition-colors duration-300 flex items-center gap-4"
                  >
                    <FaArrowLeft className="text-gray-600 group-hover:text-white transition-colors duration-300 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-600 uppercase tracking-widest mb-1">Previous</div>
                      <div className="text-white font-bold text-sm group-hover:text-gray-100 line-clamp-1">{prev.title}</div>
                    </div>
                  </Link>
                )}
                {next && (
                  <Link
                    to={`/projects/${next.slug}`}
                    className="flex-1 group glass-card rounded-2xl p-6 hover:border-gray-600 transition-colors duration-300 flex items-center justify-end gap-4 text-right"
                  >
                    <div>
                      <div className="text-xs text-gray-600 uppercase tracking-widest mb-1">Next</div>
                      <div className="text-white font-bold text-sm group-hover:text-gray-100 line-clamp-1">{next.title}</div>
                    </div>
                    <FaArrowRight className="text-gray-600 group-hover:text-white transition-colors duration-300 flex-shrink-0" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── CTA ───────────────────────────────────────── */}
        <section className="relative py-24 border-t border-gray-800/60">
          <div className="spotlight w-80 h-80 top-1/2 left-1/2" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewport}>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
                Ready to build <span className="animated-gradient-text">something like this?</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
                Let's talk about your vision and how Zynexis can turn it into a production-grade product.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 px-10 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
                >
                  Start a Project
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-2 px-10 py-4 border border-gray-700 text-gray-300 font-bold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300"
                >
                  View All Projects
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default ProjectDetail;
